'use client';

import React, { useRef } from "react";
import Headline from "@/components/ui/Headline/Headline";
import BenefitColumns from "@/components/ui/BenefitColumns/BenefitColumns";
import { LanguageType } from "@/lib/types";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import Image from "next/image";
import { useIsLg } from '@/contexts/BreakpointContext';

gsap.registerPlugin(useGSAP, ScrollTrigger);

interface Benefit {
  title: string;
  content: string;
}

interface ServiceIntroductionSectionProps {
  heading: string
  subheading: string
  imageSrc: string
  imageAlt: string
  navigationLabel: string
  language?: LanguageType
  benefits: Benefit[]
}


const ServiceIntroductionSection: React.FC<ServiceIntroductionSectionProps> = ({
  heading,
  subheading,
  imageSrc,
  navigationLabel,
  imageAlt,
  benefits
}) => {
  const isLg = useIsLg();
  const sectionRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {

    const section = sectionRef.current;
    const container = containerRef.current;
    if (!section || !container) return;

    // Horizontal padding is applied via static Tailwind classes
    // (`px-[0.5rem] lg:px-[1.5rem]` on the <section>), not gsap.set, so the
    // DOM is in its final shape before any ScrollTrigger is created.
    //
    // The "full-bleed → padded" visual is reproduced by scaling the inner
    // container via transform (composited, zero-CLS). We use the same
    // gsap.set + tl.to structure as the original padding animation — not
    // fromTo — because fromTo's eager "from" render interacts poorly with
    // scrubbed ScrollTriggers created inside the same timeline() call,
    // producing wrong start/end positions when multiple instances mount.
    const computeScaleX = () => {
      const sectionW = section.offsetWidth;
      const containerW = container.offsetWidth;
      return containerW > 0 ? sectionW / containerW : 1;
    };

    gsap.set(container, {
      scaleX: computeScaleX(),
      borderRadius: 0,
      transformOrigin: 'center center',
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top center",
        end: "center center",
        scrub: true,
        invalidateOnRefresh: true,
        // markers: true,
      }
    });

    tl.to(container, {
      scaleX: 1,
      borderRadius: isLg ? "32px" : "24px",
    });

  }, { dependencies: [isLg], revertOnUpdate: true });

  return (
    <section
      ref={sectionRef}
      id={navigationLabel}
      className="
        relative
        w-full
        h-fit
        px-[0.5rem] lg:px-[1.5rem]
      ">
      <div
        ref={containerRef}
        className="
          relative
          w-full
          overflow-hidden
          border-radius-main
          outline-secondary
          outline-stroke-soft-200
          pt-[4.5rem]
          pb-[2.5rem]
          lg:pt-[7rem]
          lg:pb-[3.5rem]
          px-[2rem] lg:px-[3.5rem]
          flex flex-col items-center
        "
        style={{
          height: isLg ? 'min(58.625rem, 100svh)' : 'fit-content'
        }}
      >
        <Image
          src={imageSrc}
          alt={imageAlt}
          fill
          sizes="100vw"
          loading="eager"
          className="
            object-cover
            object-center
            z-0
          "
        />

        <div
          style={{
            width: isLg ? '100%' : '79vw',
          height: isLg ? 'min(58.625rem, 100svh)' : 'fit-content'
          }}
          className="relative z-10 flex flex-col items-center lg:justify-between gap-[7rem]">
          <div className="flex flex-row items-center justify-center w-full">
            <Headline
              type="short"
              alignment="center"
              heading={heading}
              subheading={subheading}
              textColor="text-text-reversed"
            />
          </div>

          <div className="w-full container-large">
            <BenefitColumns
              lgGap="lg:gap-[4.5rem]"
              textColor="text-text-reversed"
              showSubheading={false}
              benefits={benefits}
            />
          </div>
        </div>
      </div>
    </section>
  )
};

export default ServiceIntroductionSection;
